import React from 'react'
import { useLuminaireEffectiveConfig } from '../../hooks/useLuminaireEffectiveConfig'
import RelaisCard from './RelaisCard'
import SanteCard from './SanteCard'
import AlertesCard from './AlertesCard'
import { Badge } from './EffConfigCard'
import styles from './LuminaireEffectiveConfigSection.module.scss'
import { formatMetricDate } from '../../../../common/utils/metricFormat'

interface Props {
  devEui: string
}

export default function LuminaireEffectiveConfigSection({ devEui }: Props) {
  const { data, isLoading, isError } = useLuminaireEffectiveConfig(devEui)

  const header = (
    <div className={styles.header}>
      <span className={styles.title}>Configuration effective / État courant</span>
      {data?.meta?.stale && (
        <div className={styles.metaRow}>
          <Badge variant="error">Périmé</Badge>
        </div>
      )}
    </div>
  )

  if (isLoading) {
    return (
      <div className={styles.wrapper}>
        {header}
        <span className={styles.stateText}>Chargement...</span>
      </div>
    )
  }
  if (isError) {
    return (
      <div className={styles.wrapper}>
        {header}
        <span className={styles.stateError}>Erreur lors du chargement</span>
      </div>
    )
  }
  if (!data) {
    return (
      <div className={styles.wrapper}>
        {header}
        <span className={styles.stateText}>Aucune donnée disponible</span>
      </div>
    )
  }

  // dimming level, RSSI downlink, SNR downlink and firmware are intentionally not
  // shown here: they are already in the top "dernières valeurs" cards (and SNR is
  // not wanted). The remaining dimming/sensor values are folded into the kept
  // groups (driver/lamp status -> Santé; luminosité + entrée dig. -> Relais/E·S).
  const { dimming, sensors, relay, health, alerts } = data
  const showRelais = relay != null || sensors != null
  const showSante = dimming != null || health != null
  const showAlertes = alerts != null
  const allNull = !showRelais && !showSante && !showAlertes

  // Hover bubble identical to the metric cards: "Capturé le: <date>".
  const capturedTooltip = data.meta?.last_seen
    ? `Capturé le: ${formatMetricDate(data.meta.last_seen)}`
    : undefined

  return (
    <div className={styles.wrapper}>
      {header}
      {allNull ? (
        <span className={styles.stateText}>Aucune donnée disponible</span>
      ) : (
        <div className={styles.cardsGrid}>
          {showRelais && <RelaisCard relay={relay} sensors={sensors} tooltip={capturedTooltip} />}
          {showSante && <SanteCard dimming={dimming} health={health} tooltip={capturedTooltip} />}
          {showAlertes && <AlertesCard alerts={alerts} tooltip={capturedTooltip} />}
        </div>
      )}
    </div>
  )
}
